import { useState, type ChangeEvent, type FormEvent } from "react";
import { topics } from "@/mocks/topics";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageLength(e.target.value.length);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/daildt3p14a1h3mr7660",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: payload.toString(),
        },
      );

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? String(serverMsg)
            : "提交失败，请稍后重试，或直接发送邮件给我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-label font-medium text-foreground-800"
          >
            称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="mt-2 w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-label font-medium text-foreground-800"
          >
            邮箱
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="mt-2 w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="contact-topic"
          className="block text-sm font-label font-medium text-foreground-800"
        >
          想聊的话题方向
        </label>
        <div className="relative mt-2">
          <select
            id="contact-topic"
            name="topic"
            defaultValue=""
            className="w-full appearance-none px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 cursor-pointer"
          >
            <option value="">内容合作 / 线索投稿</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.name}>
                关于「{topic.name}」的内容反馈
              </option>
            ))}
            <option value="纠错">内容纠错</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400 pointer-events-none"></i>
        </div>
      </div>

      <div>
        <label
          htmlFor="contact-message"
          className="block text-sm font-label font-medium text-foreground-800"
        >
          具体内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          onChange={handleMessageChange}
          placeholder="尽量说明具体话题、时间与可查证的公开来源，方便我们核实"
          className="mt-2 w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-400 font-label">
          {messageLength} / 500
        </p>
      </div>

      <input
        type="text"
        name="contact_alt"
        className="extra-line-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "success" && (
        <p className="flex items-start gap-2 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2.5">
          <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
          已收到你的消息，编辑会尽快查看并回复。
        </p>
      )}

      {status === "error" && (
        <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
        {status === "loading" ? "提交中…" : "发送消息"}
      </button>
    </form>
  );
}